import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, RefreshCw } from 'lucide-react';
import { LiveScreen, QueryState, Select, StatusBadge, formatDate, type Column } from '@/components/live';
import { dueWords, openPath, reminderLink, renewPath, type RenewalRow } from '@/lib/renewals';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { useRows } from '@/hooks/useResource';
import { DUE_STATES, optionLabel } from '@/lib/practiceLists';

const TONE: Record<RenewalRow['due_state'], 'red' | 'amber' | 'blue' | 'gray'> = { expired: 'red', due_30: 'amber', due_90: 'blue', later: 'gray' };

/**
 * Renewals due (owner, 2026-09-22: "expiry tracking system of consents ... or any record table
 * having validity aspect"). One list of everything that lapses - consents, KYC, contractor and site
 * licences, plot leases, harvest permits, land documents, soil tests, worker rate agreements, the
 * organisation's certificates - and every corrective action and complaint response that is due.
 * Each row is what the viewer may see on its own page; opening one goes there. Lapsed consents and
 * certificates are marked expired, and farmers' consent and KYC status rechecked, every night.
 */
export default function RenewalsScreen() {
  const navigate = useNavigate();
  const [stat, setStat] = useState<string | null>('soon');
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const rows = useRows<Omit<RenewalRow, 'id'>>('renewals_due', { orderBy: { column: 'due_on', ascending: true } });
  const all = useMemo(() => (rows.data ?? []).map((row) => ({ ...row, id: `${row.kind}:${row.record_id}` })) as RenewalRow[], [rows.data]);
  const kinds = useMemo(() => [...new Map(all.map((row) => [row.kind, row.kind_label])).entries()]
    .map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label)), [all]);

  const stats = useMemo(() => [
    { key: 'all', label: 'Everything dated', tone: 'neutral' as const, match: () => true },
    { key: 'soon', label: 'Expired or due in 30 days', tone: 'warn' as const, match: (row: RenewalRow) => row.due_state === 'expired' || row.due_state === 'due_30' },
    { key: 'expired', label: 'Expired', tone: 'error' as const, match: (row: RenewalRow) => row.due_state === 'expired' },
    { key: 'due_30', label: 'Due in 30 days', tone: 'warn' as const, match: (row: RenewalRow) => row.due_state === 'due_30' },
    { key: 'due_90', label: 'Due in 31-90 days', tone: 'info' as const, match: (row: RenewalRow) => row.due_state === 'due_90' },
    { key: 'actions', label: 'Actions and responses', tone: 'neutral' as const, match: (row: RenewalRow) => /_action$|_response$/.test(row.kind) },
  ], []);

  const term = search.trim().toLowerCase();
  const visible = useMemo(() => applyStat(all, stats, stat)
    .filter((row) => !kind || row.kind === kind)
    .filter((row) => !term || [row.what, row.subject, row.record_code, row.village_name, row.site_name, row.kind_label]
      .some((value) => (value ?? '').toLowerCase().includes(term))), [all, stats, stat, kind, term]);

  const columns: Array<Column<RenewalRow>> = [
    { key: 'due_on', label: 'Due', render: (row) => (
      <span className="cell-stack"><strong>{formatDate(row.due_on)}</strong>
        <small className={`due-${TONE[row.due_state]}`}>{dueWords(row.days_left)}</small></span>) },
    { key: 'kind_label', label: 'Kind', render: (row) => row.kind_label },
    { key: 'what', label: 'What', render: (row) => <span className="cell-stack"><span>{row.what}</span>{row.record_code && <small className="muted">{row.record_code}</small>}</span> },
    { key: 'subject', label: 'Who / where', render: (row) => <span className="cell-stack"><span>{row.subject ?? '--'}</span>
      <small className="muted">{[row.site_name, row.village_name].filter(Boolean).join(' · ')}</small></span> },
    { key: 'due_state', label: 'State', render: (row) => <StatusBadge label={optionLabel(DUE_STATES, row.due_state)} tone={TONE[row.due_state]} /> },
    { key: 'renew', label: '', render: (row) => {
      const path = renewPath(row);
      if (!path) return null;
      if (row.renewal_in_progress) return <small className="muted">Renewal waiting</small>;
      return (
        <button className="btn btn-ghost btn-sm" onClick={(event) => { event.stopPropagation(); navigate(path); }} type="button"
                title="Open a new record filled in from this one">
          <RefreshCw size={14} /> Renew
        </button>);
    } },
    { key: 'remind', label: '', render: (row) => {
      const link = reminderLink(row);
      return link ? (
        <a className="btn btn-ghost btn-sm" href={link} onClick={(event) => event.stopPropagation()} rel="noreferrer" target="_blank"
           title="Open WhatsApp with a reminder typed in">
          <MessageCircle size={14} /> Remind
        </a>) : null;
    } },
  ];

  return (
    <LiveScreen subtitle="Everything that lapses or falls due - consents, KYC, licences, leases, permits, tests, agreements, certificates and actions."
                title="Renewals due">
      <StatCards active={stat} onSelect={setStat} rows={all} stats={stats} />
      <div className="toolbar-row">
        <label className="field"><span>Kind</span>
          <Select onChange={setKind} options={kinds} placeholder="All kinds" value={kind} /></label>
      </div>
      <QueryState empty="Nothing dated to track yet." query={rows}>
        <DataGrid columns={columns} exportName="renewals-due" onRowClick={(row) => navigate(openPath(row))}
                  onSearchChange={setSearch} rows={visible} search={search} screenKey="party.register"
                  emptyMessage={stat === 'soon' ? 'Nothing expired or due in the next 30 days.' : 'Nothing here.'} />
      </QueryState>
    </LiveScreen>
  );
}
